import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { UsersThree, User, Buildings, Trash, Plus, Info, ShieldSlash } from "@phosphor-icons/react";

export default function PolicyAssignments({ fixedRid }) {
  const [docs, setDocs] = useState([]);
  const [rid, setRid] = useState(fixedRid || "");
  const [rules, setRules] = useState([]);
  const [preview, setPreview] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const [org, setOrg] = useState({ departments: [], teams: [], job_titles: [], projects: [] });
  const [tType, setTType] = useState("all");
  const [tId, setTId] = useState("");
  const [rbw, setRbw] = useState(false);
  const [excPid, setExcPid] = useState("");
  const [excReason, setExcReason] = useState("");

  useEffect(() => {
    if (!fixedRid) api.get("/policies").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/people").then((r) => setPeople(Array.isArray(r.data) ? r.data : r.data.people || [])).catch(() => {});
    api.get("/teams").then((r) => setTeams(Array.isArray(r.data) ? r.data : r.data.teams || [])).catch(() => {});
    api.get("/policies/org-options").then((r) => setOrg(r.data)).catch(() => {});
  }, []);

  const refresh = useCallback(() => {
    if (!rid) return;
    api.get(`/policies/${rid}/assignments`).then((r) => setRules(r.data)).catch(() => {});
    api.get(`/policies/${rid}/assignment-preview`).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    api.get(`/policies/${rid}/exceptions`).then((r) => setExceptions(r.data)).catch(() => setExceptions([]));
  }, [rid]);
  useEffect(() => { refresh(); }, [refresh]);

  const addRule = async () => {
    try {
      await api.post(`/policies/${rid}/assignments`, { target_type: tType, target_id: tType === "all" ? null : tId, require_before_work: rbw });
      toast.success("Assignment added"); setTId(""); setRbw(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const delRule = async (aid) => { await api.delete(`/policies/assignments/${aid}`); toast.success("Removed"); refresh(); };
  const addExc = async () => {
    if (!excPid || !excReason.trim()) return toast.error("Pick a person and give a reason");
    try { await api.post(`/policies/${rid}/exceptions`, { profile_id: excPid, reason: excReason, scope: "all_sources" }); toast.success("Exception recorded"); setExcPid(""); setExcReason(""); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add exception"); }
  };
  const reinstate = async (eid) => { await api.post(`/policies/exceptions/${eid}/reinstate`); toast.success("Reinstated"); refresh(); };

  const label = (t) => ({ all: "Company-wide", person: "Individual", team: "Team", department: "Department", job_title: "Job title", project: "Job/site" }[t] || t);

  return (
    <div className="max-w-4xl">
      {!fixedRid && <select data-testid="assign-doc" value={rid} onChange={(e) => setRid(e.target.value)} className="h-11 rounded-md border border-slate-200 bg-white px-3 text-sm w-full mb-4">
        <option value="">Select a document to target…</option>
        {docs.map((d) => <option key={d.id} value={d.id}>{d.title} ({d.ref})</option>)}
      </select>}

      {rid && (
        <div className="grid md:grid-cols-2 gap-5">
          {/* Rules + add */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="font-display font-bold text-slate-800 mb-3">Who must acknowledge</h3>
            <div className="flex flex-col gap-2 mb-4" data-testid="assign-rules">
              {rules.length === 0 && <p className="text-sm text-slate-400">No rules yet — this document currently defaults to company-wide.</p>}
              {rules.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
                  <span className="text-sm text-slate-700">{label(r.target_type)}{r.require_before_work ? " · before work" : ""}</span>
                  <button data-testid={`rule-del-${r.id}`} onClick={() => delRule(r.id)} className="text-slate-400 hover:text-red-600"><Trash size={16} /></button>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
              <select data-testid="rule-type" value={tType} onChange={(e) => { setTType(e.target.value); setTId(""); }} className="h-10 rounded-md border border-slate-200 px-3 text-sm">
                <option value="all">Company-wide</option>
                <option value="department">Department</option>
                <option value="team">Team</option>
                <option value="job_title">Job title</option>
                <option value="person">Individual</option>
                <option value="project">Job / site</option>
              </select>
              {tType !== "all" && (
                <select data-testid="rule-target" value={tId} onChange={(e) => setTId(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm">
                  <option value="">Pick {tType === "job_title" ? "job title" : tType === "project" ? "job/site" : tType}…</option>
                  {(tType === "department" ? org.departments
                    : tType === "team" ? (org.teams.length ? org.teams : teams)
                    : tType === "job_title" ? org.job_titles
                    : tType === "project" ? org.projects
                    : people.map((p) => ({ id: p.id, name: `${p.first_name} ${p.last_name}` }))
                  ).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="rule-rbw" checked={rbw} onChange={(e) => setRbw(e.target.checked)} /> Must acknowledge before commencing assigned work</label>
              <Button data-testid="rule-add" onClick={addRule} disabled={tType !== "all" && !tId} className="bg-emerald-700 hover:bg-emerald-800 gap-1 h-10"><Plus size={16} /> Add rule</Button>
              <p className="text-xs text-slate-400">Client/job association identifies who the document is <i>for</i>; it does not by itself assign staff. Department, team, job‑title, individual and job/site targeting are all supported; job/site (project) is kept distinct from job‑title.</p>
            </div>
          </div>

          {/* Preview + exceptions */}
          <div className="flex flex-col gap-5">
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="assign-preview">
              <h3 className="font-display font-bold text-slate-800 mb-2">Preview</h3>
              {preview ? (<>
                <div className="flex gap-4 text-sm mb-2">
                  <span className="font-semibold text-emerald-800">{preview.included_count} included</span>
                  <span className="text-slate-500">{preview.excluded_count} excluded</span>
                </div>
                <div className="max-h-40 overflow-y-auto flex flex-col gap-1">
                  {preview.included.map((p) => (
                    <div key={p.profile_id} className="flex items-center justify-between text-sm border-b border-slate-100 py-1">
                      <span className="text-slate-700">{p.name}</span>
                      <span className="text-[11px] text-slate-400 flex items-center gap-1" title={p.sources.join(", ")}><Info size={12} /> {p.sources[0]}{p.sources.length > 1 ? ` +${p.sources.length - 1}` : ""}</span>
                    </div>
                  ))}
                </div>
              </>) : <p className="text-sm text-slate-400">Loading…</p>}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="font-display font-bold text-slate-800 mb-2 flex items-center gap-2"><ShieldSlash size={17} /> Exceptions (not applicable)</h3>
              <div className="flex flex-col gap-1 mb-3" data-testid="exc-list">
                {exceptions.length === 0 && <p className="text-sm text-slate-400">No exceptions.</p>}
                {exceptions.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-sm border-b border-slate-100 py-1">
                    <span className="text-slate-700">{e.person_name} <span className="text-slate-400">· {e.reason}</span></span>
                    <button data-testid={`exc-reinstate-${e.id}`} onClick={() => reinstate(e.id)} className="text-xs font-semibold text-emerald-700 hover:underline">Reinstate</button>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
                <select data-testid="exc-person" value={excPid} onChange={(e) => setExcPid(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm"><option value="">Pick person…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}</select>
                <Input data-testid="exc-reason" value={excReason} onChange={(e) => setExcReason(e.target.value)} placeholder="Reason (required)" />
                <Button data-testid="exc-add" onClick={addExc} variant="outline" className="h-10">Mark not applicable</Button>
                <p className="text-xs text-slate-400">Exceptions survive automatic assignment refresh and preserve history. Staff cannot exempt themselves.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
